/**
 * Повестка дня календаря: что показать и в каком порядке.
 *   • сначала быстрые задачи «на весь день» (без времени), затем всё со временем по часам;
 *   • встреча занимает время (блок с длительностью), задача — нет (галочка), личное время — «Занято»;
 *   • «Просрочено» (требование владельца; с 11.10 есть и в веб-календаре CRM): мои открытые задачи с днём раньше сегодня.
 */
import type { CalendarFeed, FeedBlock, FeedMeeting, FeedTask, MeSummaryTasks, OpenTasksResponse } from '@/api/calendar';
import { addDays, dayKey, hhmm, startOf } from './calendarDates';

export type AgendaItem =
  | { kind: 'task'; id: string; time: string | null; task: FeedTask }
  | { kind: 'meeting'; id: string; time: string; meeting: FeedMeeting }
  | { kind: 'block'; id: string; time: string | null; block: FeedBlock };

const KIND_ORDER: Record<AgendaItem['kind'], number> = { block: 0, meeting: 1, task: 2 };

/** Пересекает ли отрезок местный день. */
function overlapsDay(startIso: string, endIso: string, key: string): boolean {
  const from = startOf(key).getTime();
  const to = startOf(addDays(key, 1)).getTime();
  return Date.parse(startIso) < to && Date.parse(endIso) > from;
}

const titleOf = (i: AgendaItem) => (i.kind === 'task' ? i.task.title : i.kind === 'meeting' ? i.meeting.title ?? i.meeting.guestName : i.block.title ?? '');

/** Дела одного дня по порядку: весь день → по времени. */
export function agendaFor(feed: Pick<CalendarFeed, 'tasks' | 'meetings' | 'blocks'> | undefined, key: string): AgendaItem[] {
  if (!feed) return [];
  const items: AgendaItem[] = [];
  for (const t of feed.tasks) {
    if (t.dueDate !== key) continue;
    items.push({ kind: 'task', id: t.id, time: t.startsAt ? hhmm(t.startsAt) : null, task: t });
  }
  for (const m of feed.meetings) {
    if (m.status === 'cancelled') continue;
    if (dayKey(new Date(m.startsAt)) !== key) continue;
    items.push({ kind: 'meeting', id: m.id, time: hhmm(m.startsAt), meeting: m });
  }
  for (const b of feed.blocks) {
    if (!overlapsDay(b.startsAt, b.endsAt, key)) continue;
    // Начался вчера — в этом дне он «с начала дня».
    const startsToday = dayKey(new Date(b.startsAt)) === key;
    items.push({ kind: 'block', id: b.id, time: b.allDay || !startsToday ? null : hhmm(b.startsAt), block: b });
  }
  return sortAgenda(items);
}

export function sortAgenda(items: AgendaItem[]): AgendaItem[] {
  return [...items].sort((a, b) => {
    // Без времени — наверх.
    if (!a.time !== !b.time) return a.time ? 1 : -1;
    if (a.time && b.time && a.time !== b.time) return a.time < b.time ? -1 : 1;
    if (a.kind !== b.kind) return KIND_ORDER[a.kind] - KIND_ORDER[b.kind];
    return titleOf(a).localeCompare(titleOf(b), 'ru');
  });
}

/** Точки под днём в полосе недели/месяца: до трёх; красная — несделанная задача прошлого дня, серая — сделанная. */
export function dotsFor(feed: Pick<CalendarFeed, 'tasks' | 'meetings' | 'blocks'> | undefined, key: string, today: string): Array<'' | 'r' | 'm'> {
  const items = agendaFor(feed, key);
  const past = key < today;
  return items.slice(0, 3).map((i) => {
    if (i.kind !== 'task') return '';
    if (i.task.done) return 'm';
    return past ? 'r' : '';
  });
}

export interface OverdueTask {
  id: string;
  title: string;
  dueDate: string;
  startsAt: string | null;
  entityType: string | null;
  entityId: string | null;
  entityName: string | null;
}

/** Мои открытые задачи по сегодня включительно — из какой бы ручки они ни пришли. */
export interface MyOpenTasks {
  /** Просроченных (срок раньше сегодня) — точное число, даже если список обрезан. */
  overdue: number;
  /** На сегодня — точное число. */
  dueToday: number;
  /** Список неполный (у сводки — до 20, у новой ручки — до 500). */
  truncated: boolean;
  items: OverdueTask[];
}

/** Ответ ручки «открытые задачи» (GiperCRM с 11.10.2026): все, с названием сделки. */
export function openFromServer(r: OpenTasksResponse): MyOpenTasks {
  return {
    overdue: r.overdue,
    dueToday: Math.max(0, r.total - r.overdue),
    truncated: r.truncated,
    items: r.data.map((t) => ({
      id: t.id, title: t.title, dueDate: t.dueDate, startsAt: t.startsAt,
      entityType: t.entityType, entityId: t.entityId, entityName: t.entityName ?? null,
    })),
  };
}

/** Запасной путь для старого сервера: «моя сводка» — без названий сделок, список до 20. */
export function openFromSummary(s: MeSummaryTasks): MyOpenTasks {
  return {
    overdue: s.overdue,
    dueToday: s.due_today,
    truncated: s.items.length < s.overdue + s.due_today,
    items: s.items.map((t) => ({
      id: t.id, title: t.title, dueDate: t.due_date, startsAt: t.starts_at, entityType: t.entity_type, entityId: t.entity_id, entityName: null,
    })),
  };
}

/** Просроченные из моих открытых: только дни раньше сегодня, сначала самые старые; перенесённые и отмеченные — скрыты. */
export function overdueFromOpen(s: MyOpenTasks | undefined, today: string, hidden: string[] = []): OverdueTask[] {
  if (!s) return [];
  return s.items.filter((t) => t.dueDate < today && !hidden.includes(t.id)).sort(byDue);
}

/** Запасной путь (сводка недоступна): выдача календаря за прошлые дни — мои несделанные задачи. */
export function overdueFromFeed(tasks: FeedTask[] | undefined, today: string, hidden: string[] = []): OverdueTask[] {
  return (tasks ?? [])
    .filter((t) => t.own && !t.done && t.dueDate < today && !hidden.includes(t.id))
    .map((t) => ({ id: t.id, title: t.title, dueDate: t.dueDate, startsAt: t.startsAt, entityType: t.entityType, entityId: t.entityId, entityName: t.entityName }))
    .sort(byDue);
}

function byDue(a: OverdueTask, b: OverdueTask): number {
  if (a.dueDate !== b.dueDate) return a.dueDate < b.dueDate ? -1 : 1;
  return (a.startsAt ?? '').localeCompare(b.startsAt ?? '');
}

/** Значок «Календаря»: просроченные + сегодняшние открытые (мои). */
export function calendarBadge(s: MyOpenTasks | undefined): number {
  return s ? s.overdue + s.dueToday : 0;
}

/**
 * «На сегодня»: день — сегодня; было время — то же время, но сегодня (иначе задача ушла бы во «весь день»).
 */
export function moveToTodayPatch(t: Pick<OverdueTask, 'startsAt'>, today: string): { dueDate: string; startsAt: string | null } {
  if (!t.startsAt) return { dueDate: today, startsAt: null };
  const old = new Date(t.startsAt);
  const [y, m, d] = today.split('-').map(Number);
  return { dueDate: today, startsAt: new Date(y, m - 1, d, old.getHours(), old.getMinutes(), 0, 0).toISOString() };
}

/** Отметка «сделано»/«вернуть» в кэше выдачи — сразу, до ответа сервера. */
export function withTaskDone<T extends Pick<CalendarFeed, 'tasks'>>(feed: T | undefined, id: string, done: boolean): T | undefined {
  if (!feed) return feed;
  return { ...feed, tasks: feed.tasks.map((t) => (t.id === id ? { ...t, done, doneReason: done ? t.doneReason : null } : t)) };
}
